import './applicantSuccessPage.scss';

import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import heroesCheering from '../../../assets/heroes-cheering.svg';
import logo from '../../../assets/logo-default.svg';
import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { resetStore } from '../../../store/_core/resetStore';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useDashboardStore from '../../../store/dashboardStore/useDashboardStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigateWithCallback = useNavigateWithCallback();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();
  const {
    computed: { vehicleId },
  } = useDashboardStore();

  const formSubmit = (event: FormEvent): void => {
    event.preventDefault();

    navigateWithCallback(`/dashboard/application/${vehicleId}`, {
      replace: true,
    }).then(() => {
      resetStore(useApplicantStore);
      resetStore(useCoapplicantStore);
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-success-page" sx={theme.pageLayout}>
        <header data-testid={locator.pages.applicantSuccess.header.root}>
          <img alt={t('imageAlts.logo')} src={logo} />
          <button
            aria-label="Toggle drawer"
            data-testid={locator.pages.applicantSuccess.header.drawerButton}
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.applicantSuccess.progress.root}
            value={useWorkflowStore((state) => state.computed.progress)}
            variant="determinate"
          />
        </div>

        <main>
          <figure>
            <img alt={t('imageAlts.heroesCheering')} src={heroesCheering} />
          </figure>
          <form
            data-testid={locator.pages.applicantSuccess.form.root}
            noValidate
            onSubmit={formSubmit}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantSuccess.form.h1}>
                {t('ApplicantSuccessPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantSuccess.form.h2}>
                {t('ApplicantSuccessPage.subtitle')}
              </h2>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={locator.pages.applicantSuccess.form.submitButton}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantSuccessPage.dashboard')}
                </LoadingButton>
              </div>
            </div>
            <footer data-testid={locator.pages.applicantSuccess.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantSuccessPage';
